import { Alert, Button, Typography } from '@mui/material';
import { Box } from '@mui/system';
import { useNavigate } from '@tanstack/react-router';
import { Controller, useFormContext, useFormState } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { LoadingButton } from 'src/components/atoms/loading-button/loading-button';
import PasswordInput from 'src/components/atoms/password-input/password-input';
import { useKeyDown } from 'src/hooks/use-key-down';
import { Logo } from 'src/icons/logo';
import { PasswordStepFormValues } from '../../../schema/use-form-validation-schema';
import AuthFormWrapper from '../../templates/auth-form-wrapper';
import AuthFormHeader from '../auth-form-header';

interface Props {
  onSubmit: (values: PasswordStepFormValues) => Promise<void>;
  onBackButtonClick: () => void;
}

export const PasswordStep = ({ onSubmit, onBackButtonClick }: Props) => {
  const { control, handleSubmit } = useFormContext<PasswordStepFormValues>();
  const { isValid, isSubmitting, errors } =
    useFormState<PasswordStepFormValues>();

  const { t } = useTranslation();
  const navigate = useNavigate();

  useKeyDown('Enter', handleSubmit(onSubmit));

  return (
    <AuthFormWrapper
      actionsSlot={
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: 2,
          }}>
          <Button variant="outlined" onClick={onBackButtonClick}>
            {t('common:button.back')}
          </Button>
          <LoadingButton
            variant="contained"
            size="medium"
            disabled={!isValid}
            loading={isSubmitting}
            onClick={handleSubmit(onSubmit)}>
            {t('auth:login.loginButton')}
          </LoadingButton>
        </Box>
      }>
      <AuthFormHeader
        title={t('auth:login.title')}
        description={t('auth:login.contentPassword')}
        icon={<Logo />}
      />
      <Box>
        <Controller
          name="password"
          control={control}
          render={({
            field: { onChange, onBlur, value, ref },
            fieldState: { error },
          }) => (
            <PasswordInput
              label={t('auth:password')}
              fullWidth
              required
              inputRef={ref}
              onBlur={onBlur}
              onChange={onChange}
              value={value}
              type="password"
              autoComplete="password"
              error={!!error}
              helperText={error?.message}
              sx={{ mb: 1 }}
            />
          )}
        />
        {errors?.root?.message ? (
          <Alert severity="error">{errors?.root?.message}</Alert>
        ) : null}
        <Typography
          variant="subtitle2"
          sx={{ cursor: 'pointer', color: '#6EBAD3' }}
          onClick={() =>
            navigate({
              to: '/forgot-password',
            })
          }>
          {t('auth:login.forgotYourPassword')}
        </Typography>
      </Box>
    </AuthFormWrapper>
  );
};
